// Single source of truth for what Maviinci sells. Keys must stay identical to
// the storefront (routes, search vocabulary, customizer categories) — the
// website reads `category`, `productKind` and the tag keys saved from here.
import vocabulary from './catalogVocabulary.json';

export { vocabulary };

export const PRODUCT_KINDS = [
    {
        value: 'sample',
        label: 'Designer sample',
        icon: '◆',
        title: 'Designer Sample',
        blurb: 'One stitched sample with fixed fabrics and style. Every order is made to the customer\'s measurements.',
    },
    {
        value: 'customizer',
        label: 'Customizer link',
        icon: '✦',
        title: 'Customizer Product',
        blurb: 'Imported from a customizer link. Customers can change fabrics and style; price follows the fabrics.',
    },
    {
        value: 'accessory',
        label: 'Accessory',
        icon: '❖',
        title: 'Accessory',
        blurb: 'Ready item (tie, cap, brooch…). No fabric or measurement needed — just photos, price and stock.',
    },
];

// measurement: 0 = upper body, 1 = lower body, 2 = both. Mirrors storefront `Types[cat].measurement`.
// fabricGroup: legacy per-category doc under `Fabric/{group}/fabrics`; the master list lives in `Fabric/Suits/fabrics`.
// customizer: the `/custom/{route}` the storefront customizer understands; null = sample only.
export const GARMENT_CATEGORIES = [
    { value: 'suit', label: 'Suit', pieces: ['Coat', 'Pant', 'Vest', 'Shirt'], defaultPieces: ['Coat', 'Pant'], measurement: 2, fabricGroup: 'Suits', customizer: 'suit', deliveryDays: 15 },
    { value: 'blazer', label: 'Blazer / Coat', pieces: ['Coat'], defaultPieces: ['Coat'], measurement: 0, fabricGroup: 'blazer', customizer: 'blazer', deliveryDays: 12 },
    { value: 'sherwani', label: 'Sherwani', pieces: ['Sherwani', 'Pajama', 'Kurta'], defaultPieces: ['Sherwani', 'Pajama'], measurement: 2, fabricGroup: 'kurta', customizer: null, deliveryDays: 20 },
    { value: 'indo_western', label: 'Indo-western', pieces: ['Indo-western', 'Pant', 'Kurta'], defaultPieces: ['Indo-western', 'Pant'], measurement: 2, fabricGroup: 'kurta', customizer: null, deliveryDays: 18 },
    { value: 'kurta', label: 'Kurta set', pieces: ['Kurta', 'Pajama', 'Sadri', 'Coat'], defaultPieces: ['Kurta', 'Pajama'], measurement: 2, fabricGroup: 'kurta', customizer: 'kurta', importAliases: ['ethnic'], deliveryDays: 10 },
    { value: 'only_kurta', label: 'Kurta', pieces: ['Kurta'], defaultPieces: ['Kurta'], measurement: 0, fabricGroup: 'kurta', customizer: 'only_kurta', deliveryDays: 8 },
    { value: 'pajama', label: 'Pajama', pieces: ['Pajama'], defaultPieces: ['Pajama'], measurement: 1, fabricGroup: 'kurta', customizer: 'pajama', deliveryDays: 7 },
    { value: 'sadri', label: 'Sadri / Nehru jacket', pieces: ['Sadri'], defaultPieces: ['Sadri'], measurement: 0, fabricGroup: 'kurta', customizer: 'sadri', deliveryDays: 8 },
    { value: 'jubba', label: 'Jubba', pieces: ['Jubba', 'Pajama'], defaultPieces: ['Jubba'], measurement: 2, fabricGroup: 'kurta', customizer: null, deliveryDays: 10 },
    { value: 'vestcoat', label: 'Vest coat', pieces: ['Vest'], defaultPieces: ['Vest'], measurement: 0, fabricGroup: 'Suits', customizer: 'vestcoat', deliveryDays: 8 },
    { value: 'formal', label: 'Shirt & trouser set', pieces: ['Shirt', 'Pant'], defaultPieces: ['Shirt', 'Pant'], measurement: 2, fabricGroup: 'formal', customizer: 'formal', deliveryDays: 10 },
    { value: 'shirt', label: 'Shirt', pieces: ['Shirt'], defaultPieces: ['Shirt'], measurement: 0, fabricGroup: 'formal', customizer: 'shirt', deliveryDays: 7 },
    { value: 'pant', label: 'Trouser', pieces: ['Pant'], defaultPieces: ['Pant'], measurement: 1, fabricGroup: 'formal', customizer: 'pant', deliveryDays: 7 },
    { value: 'safari', label: 'Safari suit', pieces: ['Safari jacket', 'Pant'], defaultPieces: ['Safari jacket', 'Pant'], measurement: 2, fabricGroup: 'formal', customizer: null, deliveryDays: 12 },
    { value: 'apron', label: 'Apron / Chef coat', pieces: ['Apron'], defaultPieces: ['Apron'], measurement: 0, fabricGroup: 'formal', customizer: null, deliveryDays: 7 },
    // added with the catalogue plan (docs/catalog-plan.md in the website repo)
    { value: 'pathani', label: 'Pathani', pieces: ['Kurta', 'Salwar'], defaultPieces: ['Kurta', 'Salwar'], measurement: 2, fabricGroup: 'kurta', customizer: null, deliveryDays: 10 },
    { value: 'jodhpuri', label: 'Jodhpuri / Bandhgala', pieces: ['Coat', 'Pant'], defaultPieces: ['Coat', 'Pant'], measurement: 2, fabricGroup: 'Suits', customizer: null, deliveryDays: 15 },
    { value: 'tuxedo', label: 'Tuxedo', pieces: ['Coat', 'Pant', 'Vest', 'Shirt'], defaultPieces: ['Coat', 'Pant'], measurement: 2, fabricGroup: 'Suits', customizer: null, deliveryDays: 15 },
    { value: 'uniform_school', label: 'School uniform', pieces: ['Shirt', 'Pant', 'Skirt', 'Blazer'], defaultPieces: ['Shirt', 'Pant'], measurement: 2, fabricGroup: 'formal', customizer: null, deliveryDays: 15 },
    { value: 'uniform_corporate', label: 'Corporate uniform', pieces: ['Shirt', 'Pant', 'Blazer'], defaultPieces: ['Shirt', 'Pant'], measurement: 2, fabricGroup: 'formal', customizer: null, deliveryDays: 15 },
    { value: 'uniform_hotel', label: 'Hotel uniform', pieces: ['Coat', 'Shirt', 'Pant', 'Apron'], defaultPieces: ['Coat', 'Pant'], measurement: 2, fabricGroup: 'formal', customizer: null, deliveryDays: 15 },
    { value: 'uniform_security', label: 'Security uniform', pieces: ['Shirt', 'Pant'], defaultPieces: ['Shirt', 'Pant'], measurement: 2, fabricGroup: 'formal', customizer: null, deliveryDays: 15 },
    { value: 'uniform_hospital', label: 'Hospital uniform', pieces: ['Top', 'Pant', 'Lab coat'], defaultPieces: ['Top', 'Pant'], measurement: 2, fabricGroup: 'formal', customizer: null, deliveryDays: 15 },
];

export const ACCESSORY_CATEGORY = 'accessory';

export const ACCESSORY_TYPES = Object.entries(vocabulary.accessories).map(([value, v]) => ({ value, label: v.label }));

// Pieces the customizer prices by position; extra pieces fall back to 0.
export const PIECE_POSITIONS = {
    suit: { Coat: 0, Shirt: 4, Pant: 1, Vest: 2 },
    pant: { Pant: 0 },
    shirt: { Shirt: 0 },
    formal: { Shirt: 1, Pant: 0 },
    blazer: { Coat: -2 },
    kurta: { Kurta: 1, Coat: 0, Pajama: 2, Sadri: 0 },
    sadri: { Sadri: 0 },
    only_kurta: { Kurta: 1, Pajama: 2 },
    pajama: { Pajama: 0 },
    vestcoat: { Vest: 0 },
};

export const ALL_CATEGORY_OPTIONS = [
    ...GARMENT_CATEGORIES.map((c) => ({ value: c.value, label: c.label })),
    { value: ACCESSORY_CATEGORY, label: 'Accessory' },
];

export const getCategory = (value) => GARMENT_CATEGORIES.find((c) => c.value === value) || null;

export const getCategoryLabel = (value) => {
    if (value === ACCESSORY_CATEGORY) return 'Accessory';
    const c = getCategory(value);
    if (c) return c.label;
    return value ? String(value).replace(/_/g, ' ').replace(/^\w/, (ch) => ch.toUpperCase()) : 'Product';
};

export const getAccessoryLabel = (value) => vocabulary.accessories[value]?.label || '';

export const CUSTOMIZER_CATEGORIES = GARMENT_CATEGORIES.filter((c) => c.customizer);

// Category key from a customizer URL segment (`/custom/ethnic/...` → kurta).
export const categoryFromCustomizerRoute = (route) => {
    const r = String(route || '').toLowerCase();
    const hit = GARMENT_CATEGORIES.find((c) => c.customizer && (c.customizer === r || c.value === r || (c.importAliases || []).includes(r)));
    return hit ? hit.value : null;
};

// Older documents have no productKind; infer it the same way the storefront does.
export const inferProductKind = (doc) => {
    if (!doc) return 'sample';
    if (doc.productKind) return doc.productKind;
    if (doc.accessoryType || doc.category === ACCESSORY_CATEGORY) return 'accessory';
    if (doc.customize) return 'customizer';
    return 'sample';
};

export const getKindLabel = (kind) => (kind === 'uniform' ? 'Uniform' : PRODUCT_KINDS.find((k) => k.value === kind)?.label || 'Designer sample');

const toOptions = (group) => Object.entries(vocabulary[group]).map(([value, v]) => ({ value, label: v.label }));

export const OCCASION_OPTIONS = toOptions('occasions');
export const FESTIVAL_OPTIONS = toOptions('festivals');
export const ROLE_OPTIONS = toOptions('roles');
export const VIBE_OPTIONS = toOptions('vibes');

// `pastel` is a search concept that spans families, not a family of its own.
export const COLOR_FAMILY_OPTIONS = Object.entries(vocabulary.colors)
    .filter(([value]) => value !== 'pastel')
    .map(([value, v]) => ({ value, label: v.label }));

export const COLOR_SWATCH = {
    red: '#c0392b', maroon: '#6d1a24', blue: '#2f6fd1', navy: '#1b2a4a', green: '#2e7d4f', olive: '#6b6b2a',
    brown: '#6b4226', beige: '#d8c3a0', cream: '#f3ead6', white: '#ffffff', black: '#111111', grey: '#8a8d91',
    gold: '#c9a243', pink: '#e79ab0', purple: '#6a3d9a', yellow: '#e8c53a', orange: '#e07b2a',
};

export const FORMALITY_LEVELS = [
    { value: 1, label: 'Casual' },
    { value: 2, label: 'Smart casual' },
    { value: 3, label: 'Festive' },
    { value: 4, label: 'Formal' },
    { value: 5, label: 'Ceremonial' },
];

export const labelFor = (group, key) => vocabulary[group]?.[key]?.label || key;

// Conservative starting tags per category; the admin reviews them before saving.
export const CATEGORY_TAG_DEFAULTS = {
    suit: { occasions: ['wedding', 'reception', 'engagement'], roles: ['groom_friend', 'groom_brother', 'guest'], vibe: ['classic'], formality: 4 },
    blazer: { occasions: ['party', 'cocktail', 'reception'], roles: ['groom_friend', 'guest'], vibe: ['modern'], formality: 4 },
    sherwani: { occasions: ['wedding', 'reception'], roles: ['groom'], vibe: ['royal'], formality: 5 },
    indo_western: { occasions: ['sangeet', 'reception'], roles: ['groom_friend', 'groom_brother'], vibe: ['modern', 'statement'], formality: 4 },
    kurta: { occasions: ['festive', 'puja', 'wedding'], festivals: ['diwali', 'eid'], roles: ['family', 'guest'], vibe: ['traditional'], formality: 3 },
    only_kurta: { occasions: ['festive', 'puja', 'daily'], festivals: ['eid', 'diwali'], vibe: ['traditional'], formality: 2 },
    pajama: { occasions: ['festive', 'daily'], vibe: ['traditional'], formality: 2 },
    sadri: { occasions: ['wedding', 'festive', 'sangeet'], festivals: ['diwali'], roles: ['family', 'groom_brother'], vibe: ['traditional', 'classic'], formality: 3 },
    jubba: { occasions: ['festive', 'puja'], festivals: ['eid'], roles: ['family'], vibe: ['traditional'], formality: 3 },
    vestcoat: { occasions: ['party', 'reception'], vibe: ['classic'], formality: 4 },
    formal: { occasions: ['office', 'interview'], roles: ['professional'], vibe: ['classic', 'minimal'], formality: 3 },
    shirt: { occasions: ['office', 'daily'], roles: ['professional'], vibe: ['classic'], formality: 3 },
    pant: { occasions: ['office', 'daily'], roles: ['professional'], vibe: ['classic'], formality: 3 },
    safari: { occasions: ['office'], roles: ['professional'], vibe: ['classic'], formality: 3 },
    apron: { occasions: ['daily'], roles: ['professional'], formality: 1 },
    pathani: { occasions: ['festive', 'puja', 'daily'], festivals: ['eid'], roles: ['family', 'guest'], vibe: ['traditional'], formality: 3 },
    jodhpuri: { occasions: ['wedding', 'reception', 'engagement'], roles: ['groom', 'groom_brother', 'groom_father', 'guest'], vibe: ['royal', 'classic'], formality: 5 },
    tuxedo: { occasions: ['reception', 'cocktail', 'party'], roles: ['groom', 'groom_friend', 'guest'], vibe: ['classic', 'statement'], formality: 5 },
    uniform_school: { occasions: ['daily'], roles: ['student'], formality: 2 },
    uniform_corporate: { occasions: ['office', 'daily'], roles: ['professional'], formality: 3 },
    uniform_hotel: { occasions: ['daily'], roles: ['professional'], formality: 2 },
    uniform_security: { occasions: ['daily'], roles: ['professional'], formality: 2 },
    uniform_hospital: { occasions: ['daily'], roles: ['professional'], formality: 2 },
};

export const ACCESSORY_TAG_DEFAULTS = {
    tie: { occasions: ['office', 'wedding', 'interview'], vibe: ['classic'], formality: 4 },
    bow_tie: { occasions: ['reception', 'cocktail', 'party'], vibe: ['statement'], formality: 4 },
    pocket_square: { occasions: ['wedding', 'reception', 'party'], vibe: ['classic'], formality: 4 },
    cufflinks: { occasions: ['wedding', 'office', 'reception'], vibe: ['classic'], formality: 4 },
    tie_pin: { occasions: ['office', 'wedding'], vibe: ['classic'], formality: 4 },
    brooch: { occasions: ['wedding', 'reception', 'engagement'], vibe: ['royal'], formality: 4 },
    safa: { occasions: ['wedding'], roles: ['groom', 'groom_father', 'family'], vibe: ['royal', 'traditional'], formality: 5 },
    cap: { occasions: ['festive', 'puja'], festivals: ['eid'], vibe: ['traditional'], formality: 3 },
    shawl: { occasions: ['wedding', 'festive'], vibe: ['royal', 'traditional'], formality: 4 },
    hat: { occasions: ['party', 'daily'], vibe: ['modern'], formality: 2 },
    belt: { occasions: ['office', 'daily'], vibe: ['classic'], formality: 3 },
    dupatta: { occasions: ['wedding', 'reception'], roles: ['groom'], vibe: ['royal'], formality: 5 },
    mala: { occasions: ['wedding'], roles: ['groom'], vibe: ['royal'], formality: 5 },
    kalgi: { occasions: ['wedding'], roles: ['groom'], vibe: ['royal'], formality: 5 },
    sarpech: { occasions: ['wedding'], roles: ['groom'], vibe: ['royal'], formality: 5 },
    sehra: { occasions: ['wedding'], roles: ['groom'], vibe: ['royal', 'traditional'], formality: 5 },
    kamarbandh: { occasions: ['wedding'], roles: ['groom'], vibe: ['royal'], formality: 5 },
    mojari: { occasions: ['wedding', 'festive'], roles: ['groom', 'family', 'guest'], vibe: ['traditional'], formality: 4 },
    handkerchief: { occasions: ['office', 'daily'], vibe: ['classic'], formality: 3 },
    lapel_pin: { occasions: ['wedding', 'reception', 'party'], vibe: ['classic'], formality: 4 },
    suspenders: { occasions: ['party', 'reception'], vibe: ['classic'], formality: 4 },
    socks: { occasions: ['office', 'daily'], vibe: ['classic'], formality: 3 },
    gamchha: { occasions: ['daily', 'festive'], vibe: ['traditional'], formality: 1 },
    shemagh: { occasions: ['daily', 'festive'], festivals: ['eid'], vibe: ['traditional'], formality: 2 },
    gift_set: { occasions: ['wedding', 'festive'], vibe: ['classic'], formality: 4 },
};

// Accessories that suit each garment, used to suggest "pairs with" links.
export const PAIRING_HINTS = {
    suit: ['tie', 'pocket_square', 'cufflinks', 'tie_pin', 'belt', 'bow_tie'],
    blazer: ['pocket_square', 'brooch', 'tie', 'belt'],
    sherwani: ['safa', 'brooch', 'shawl'],
    indo_western: ['brooch', 'pocket_square'],
    kurta: ['shawl', 'cap', 'brooch'],
    only_kurta: ['cap', 'shawl'],
    sadri: ['brooch', 'pocket_square'],
    jubba: ['cap'],
    vestcoat: ['tie', 'pocket_square', 'tie_pin'],
    formal: ['tie', 'belt', 'cufflinks'],
    shirt: ['tie', 'cufflinks'],
    pant: ['belt'],
    safari: ['belt'],
};
